import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, RefreshCw, Trash2, Inbox } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { usePageMeta } from "@/seo/usePageMeta";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function formatError(err) {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(" ");
  if (typeof detail === "string") return detail;
  return "Something went wrong. Please try again.";
}

export default function Admin() {
  usePageMeta({ title: "Admin | SafeHome Security Solutions Inc.", path: "/admin", noindex: true });
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);

  const fetchQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes`);
      setQuotes(data.quotes);
    } catch (err) {
      if (err.response?.status === 401) setUser(null);
      else toast.error("Could not load quotes", { description: formatError(err) });
    } finally {
      setQuotesLoading(false);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) fetchQuotes();
  }, [user, fetchQuotes]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
    } catch (err) {
      setLoginError(formatError(err));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(null);
    setQuotes([]);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/quotes/${id}`);
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Quote request deleted");
    } catch (err) {
      toast.error("Delete failed", { description: formatError(err) });
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="admin-login-page">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-white border border-zinc-200 rounded-md p-10 shadow-sm"
        >
          <div className="flex items-center gap-4 mb-8">
            <Logo dark />
            <div>
              <h1 className="text-xl font-extrabold tracking-tight text-zinc-900">Admin Login</h1>
              <p className="text-xs text-zinc-500">SafeHome Security Solutions Inc.</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@safehomesecurity.ca"
            />
          </div>
          <div className="space-y-2 mt-5">
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </div>

          {loginError && (
            <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-2.5">
              {loginError}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit-btn"
            disabled={loginLoading}
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-brand-primary bg-brand-primary-hover text-black font-semibold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60"
          >
            {loginLoading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loginLoading ? "Signing in…" : "Sign In"}
          </button>
          <a href="/" data-testid="admin-back-home-link" className="mt-5 block text-sm text-zinc-400 hover:text-brand-primary transition-colors">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-zinc-200">
        <div className="container-x h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo dark />
            <div>
              <h1 className="text-lg font-extrabold tracking-tight text-zinc-900 leading-none">Quote Requests</h1>
              <p className="text-xs text-zinc-500">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchQuotes}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${quotesLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-primary bg-brand-primary-hover text-black text-sm font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-12">
        <p className="text-sm text-zinc-500 mb-8" data-testid="admin-quote-count">
          {quotes.length} quote request{quotes.length === 1 ? "" : "s"} received
        </p>

        {quotes.length === 0 && !quotesLoading && (
          <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="admin-empty-state">
            <Inbox className="w-12 h-12 text-zinc-300 mb-4" />
            <p className="text-lg font-semibold text-zinc-700">No quote requests yet</p>
            <p className="text-sm text-zinc-500 mt-1">New submissions from the website will appear here.</p>
          </div>
        )}

        <div className="space-y-5">
          {quotes.map((q) => (
            <article
              key={q.id}
              data-testid={`quote-item-${q.id}`}
              className="bg-white border border-zinc-200 rounded-md p-6 lg:p-8 shadow-sm"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-bold tracking-tight text-zinc-900">{q.name}</h2>
                  <p className="text-sm text-zinc-500 mt-1">
                    <a href={`tel:${q.phone}`} className="hover:text-brand-primary">{q.phone}</a>
                    {" · "}
                    <a href={`mailto:${q.email}`} className="hover:text-brand-primary">{q.email}</a>
                    {q.city && ` · ${q.city}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-3 py-1 rounded-md bg-brand-primary/10 text-brand-primary text-xs font-semibold">
                    {q.service}
                  </span>
                  <button
                    onClick={() => handleDelete(q.id)}
                    data-testid={`quote-delete-btn-${q.id}`}
                    className="p-2 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    aria-label="Delete quote request"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {q.message && <p className="mt-4 text-sm leading-relaxed text-zinc-600">{q.message}</p>}
              <p className="mt-4 text-xs text-zinc-500">
                Received {new Date(q.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
